import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";

// Components
import OverallProgress from "../../../Courses/OverallProgress";
import CourseCard from "../../../Courses/CourseCard";
import Spinner from "../../../components/spinner";
import Button from "../../../components/button";

// Styles
import styles from "./index.module.scss";

// Assets
import StudyPathImg from 'images/icons/studypath.png'

// Utils
import { coursesArchivedUrl, coursesActiveUrl, coursesLatestUrl, coursesNewUrl, coursesTotalProgressUrl } from '../../../utils/urls'
import ThemeProvider from "../../../utils/theme";

const CoursesIndex = () => {
  const { t } = useTranslation();

  // State
  const [activeCourses, setActiveCourses] = useState([]);
  const [archivedCourses, setArchivedCourses] = useState([]);
  const [latestActivity, setLatestActivity] = useState(null);
  const [globalStreak, setGlobalStreak] = useState(0);
  const [loadingLatestActivity, setLoadingLatestActivity] = useState(false);
  const [loadingActiveCourses, setLoadingActiveCourses] = useState(false);
  const [loadingArchivedCourses, setLoadingArchivedCourses] = useState(false);

  // Methods
  const fetchArchivedCourses = async () => {
    try {
      setLoadingArchivedCourses(true);
      const options = { method: 'GET', headers: { 'Accept': 'application/json' } }
      const response = await fetch(coursesArchivedUrl(), options);
      if (response.ok) {
        const data = await response.json();
        setArchivedCourses(data.data.map(course => (course.attributes)));
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingArchivedCourses(false);
    }
  }

  const fetchActiveCourses = async () => {
    try {
      setLoadingActiveCourses(true);
      const options = { method: 'GET', headers: { 'Accept': 'application/json' } }
      const response = await fetch(coursesActiveUrl(), options);
      if (response.ok) {
        const data = await response.json();
        setActiveCourses(data.data.map(course => (course.attributes)));
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingActiveCourses(false);
    }
  }

  const fetchLatestActivity = async () => {
    try {
      setLoadingLatestActivity(true);
      const options = { method: 'GET', headers: { 'Accept': 'application/json' } }
      const response = await fetch(coursesLatestUrl(), options);
      if (response.ok) {
        const data = await response.json();
        setLatestActivity(data.data.attributes);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingLatestActivity(false);
    }
  }

  const fetchGlobalProgress = async () => {
    try {
      const options = { method: 'GET', headers: { 'Accept': 'application/json' } }
      const response = await fetch(coursesTotalProgressUrl(), options);
      if (response.ok) {
        const data = await response.json();
        setGlobalStreak(data.streak || 0);
      }
    } catch (error) {
      console.error(error);
    }
  }

  // Effects
  useEffect(() => {
    fetchArchivedCourses();
    fetchActiveCourses();
    fetchLatestActivity();
    fetchGlobalProgress();
  }, []);

  // handlers
  const handleCreateStudyPath = () => {
    Turbo.visit(coursesNewUrl());
  }

  // Render

  const renderArchivedCourses = () => archivedCourses.map((course) => (
    <CourseCard key={course.id} course={course} archived fixedWidth />
  ))

  const renderActiveCourses = () => activeCourses.map((course) => (
    <CourseCard key={course.id} course={course} fixedWidth />
  ))

  // EMPTY STATE
  if (!loadingLatestActivity && !loadingActiveCourses && !loadingArchivedCourses && !latestActivity && activeCourses.length === 0 && archivedCourses.length === 0) {
    return (
      <div className='flex flex-col justify-center items-center gap-4 w-full bg-white dark:bg-purple-900 rounded-lg p-4 flex-1'>
        <img src={StudyPathImg} alt="Study Path" />

        <h1 className="text-2xl text-center">
          {t('courses.index.no_study_paths')}
        </h1>
        <p className="text-gray-500 dark:text-gray-300 text-center">
          {t('courses.index.create_new_one')}
        </p>

        <Button type='primary' className='rounded-full' onClick={handleCreateStudyPath}>
          {t('courses.index.create_study_path')}
        </Button>
      </div >
    )
  }

  const textStyle = 'text-black dark:text-white';
  return (
    <ThemeProvider>
      <div className={styles.container}>
        <div className={`${styles.header} rounded-lg p-4`}>
          <h1 className={`${textStyle} text-2xl`}>
            {t('courses.index.title')}
          </h1>
        </div>

        <OverallProgress isShow={false} />

        <div className={styles.latestContainer}>
          {/* Hiding ultimate progression for now */}
          {/* <div className={`${styles.ultimateProgression} basis-1/3`}>
            <h2 className={`${textStyle} text-md`}>
              {t('courses.index.ultimate_progression')}
            </h2>

            <CourseCard
              course={{
                id: "all-courses",
                title: t('courses.index.all_courses'),
                description: t('courses.index.all_courses_description'),
                start_date: dayjs().format('YYYY-MM-DD'),
                end_date: dayjs().format('YYYY-MM-DD'),
                course_depth: 0,
                key_dates: [],
                streak: globalStreak,
              }}
              allCourses
            />
          </div> */}

          <div className={`${styles.latestActivity} basis-2/3`}>
            <h2 className={`${textStyle} text-md`}>
              {t('courses.index.latest_activity')}
            </h2>

            {loadingLatestActivity ? <Spinner /> : (latestActivity ? (<CourseCard course={latestActivity} latestActivity />) : <p className="text-gray-500 dark:text-gray-300 text-center">
              {t('courses.index.no_latest_activity')}
            </p>)}

          </div>
        </div>

        <div className={styles.activeCoursesContainer}>
          <h2 className={`${textStyle} text-md`}>
            {t('courses.index.active_courses')}
          </h2>

          <div className={styles.coursesListContainer}>
            {loadingActiveCourses ? <Spinner /> : (activeCourses.length > 0 ? renderActiveCourses() : <p className="text-gray-500 dark:text-gray-300 text-center">
              {t('courses.index.no_active_courses')}
            </p>)}
          </div>
        </div>

        <div className={styles.archivedCoursesContainer}>
          <h2 className={`${textStyle} text-md`}>
            {t('courses.index.archived_courses')}
          </h2>

          <div className={styles.coursesListContainer}>
            {loadingArchivedCourses ? <Spinner /> : (archivedCourses.length > 0 ? renderArchivedCourses() : <p className="text-gray-500 dark:text-gray-300 text-center">
              {t('courses.index.no_archived_courses')}
            </p>)}
          </div>
        </div>
      </div>
    </ThemeProvider>
  )
}

export default CoursesIndex;
